import { apiFetch, apiUrl } from "@/lib/api/client";
import { isDemoMode, fetchDemoFixture } from "@/lib/api/demoMode";
import { captureSession, sessionSignal } from "@/lib/api/session";
import { toV2PredictionRequest } from "@/features/prediction/api/predictionRequestV2";
import type { PredictionFormValues } from "@/features/prediction/model/form";
import type {
  CannedPrediction,
  JsonObject,
  PredictionPayload,
  PredictionRequest,
  PredictionStreamEvent,
  ProductArtifactModule,
} from "@/features/prediction/model/types";
import { PRODUCT_ARTIFACT_MODULES } from "@/features/prediction/model/types";
import { V4_SIMULATION_MODULES } from "@/lib/v4Pipeline";

export { toV2PredictionRequest } from "@/features/prediction/api/predictionRequestV2";

const PREDICTION_STREAM_PATH = "/api/v2/predict/stream";

/** 保证每个工作台模拟步骤至少保持 2 秒运行态。 */
const SIMULATION_STEP_MIN_DURATION_MS = 2_000;
const SIMULATION_STEP_BOUNDARIES = V4_SIMULATION_MODULES.map((module) => ({
  id: module.id,
  entryPhase: module.phases[0],
  completionPhase: module.phases[module.phases.length - 1],
}));
const SIMULATION_STEP_BY_ENTRY_PHASE = new Map<string, string>(
  SIMULATION_STEP_BOUNDARIES.map((step) => [step.entryPhase, step.id]),
);
const SIMULATION_STEP_BY_COMPLETION_PHASE = new Map<string, string>(
  SIMULATION_STEP_BOUNDARIES.map((step) => [step.completionPhase, step.id]),
);

export class PredictionStreamError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PredictionStreamError";
  }
}

export interface PredictionStreamOptions {
  signal: AbortSignal;
  onEvent: (event: PredictionStreamEvent) => void;
  onHistoryId?: (id: string) => void;
  demo?: boolean;
  demoSpeed?: number;
  historyReview?: PredictionFormValues;
  outputLanguage?: "zh" | "en";
}

function isPredictionStreamEvent(value: unknown): value is PredictionStreamEvent {
  if (typeof value !== "object" || value === null || !("type" in value)) {
    return false;
  }
  if (typeof value.type !== "string") return false;
  switch (value.type) {
    case "keepalive":
      return true;
    case "phase":
      return (
        "name" in value &&
        typeof value.name === "string" &&
        "status" in value &&
        (value.status === "start" || value.status === "done")
      );
    case "soul_vote":
      return "done" in value && typeof value.done === "number";
    case "final":
      return "payload" in value && isJsonObject(value.payload);
    case "error":
      return !("message" in value) || typeof value.message === "string";
    case "workflow_step":
      return (
        "step" in value &&
        typeof value.step === "string" &&
        "order" in value &&
        typeof value.order === "number" &&
        "status" in value &&
        (value.status === "start" ||
          value.status === "done" ||
          value.status === "failed")
      );
    case "artifact":
      return (
        "module" in value &&
        isProductArtifactModule(value.module) &&
        "revision" in value &&
        typeof value.revision === "number" &&
        Number.isInteger(value.revision) &&
        value.revision > 0 &&
        "payload" in value &&
        isJsonObject(value.payload)
      );
    default:
      return false;
  }
}

function isJsonObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isProductArtifactModule(
  value: unknown,
): value is ProductArtifactModule {
  return (
    typeof value === "string" &&
    PRODUCT_ARTIFACT_MODULES.some((module) => module === value)
  );
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    throw new DOMException("Prediction cancelled", "AbortError");
  }
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new DOMException("Prediction cancelled", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function createSimulationStepGate(signal: AbortSignal) {
  const stepStartedAt = new Map<string, number>();

  const waitForStepCompletion = async (stepId: string): Promise<void> => {
    const startedAt = stepStartedAt.get(stepId);
    if (startedAt === undefined) return;
    const elapsed = Date.now() - startedAt;
    await delay(Math.max(0, SIMULATION_STEP_MIN_DURATION_MS - elapsed), signal);
    throwIfAborted(signal);
    stepStartedAt.delete(stepId);
  };

  return async (event: PredictionStreamEvent): Promise<void> => {
    if (event.type === "phase") {
      if (event.status === "start") {
        const stepId = SIMULATION_STEP_BY_ENTRY_PHASE.get(event.name);
        if (stepId && !stepStartedAt.has(stepId)) {
          stepStartedAt.set(stepId, Date.now());
        }
        return;
      }

      const stepId = SIMULATION_STEP_BY_COMPLETION_PHASE.get(event.name);
      if (stepId) await waitForStepCompletion(stepId);
      return;
    }

    if (event.type === "final" && stepStartedAt.size > 0) {
      const remainingTimes = Array.from(stepStartedAt.values(), (startedAt) =>
        Math.max(0, SIMULATION_STEP_MIN_DURATION_MS - (Date.now() - startedAt)),
      );
      await delay(Math.max(...remainingTimes), signal);
      throwIfAborted(signal);
      stepStartedAt.clear();
    }
  };
}

function parseLine(line: string): PredictionStreamEvent | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  return isPredictionStreamEvent(parsed) ? parsed : null;
}

function dispatchEvent(
  event: PredictionStreamEvent,
  onEvent: (event: PredictionStreamEvent) => void,
): PredictionPayload | null {
  onEvent(event);
  if (event.type === "error") {
    throw new PredictionStreamError(event.message || "Prediction stream error");
  }
  return event.type === "final" ? event.payload : null;
}

async function dispatchEventWithSimulationStepDelay(
  event: PredictionStreamEvent,
  onEvent: (event: PredictionStreamEvent) => void,
  gate: ((event: PredictionStreamEvent) => Promise<void>) | null,
): Promise<PredictionPayload | null> {
  if (gate) await gate(event);
  return dispatchEvent(event, onEvent);
}

async function replayCannedPrediction(
  request: PredictionRequest,
  options: PredictionStreamOptions,
): Promise<PredictionPayload> {
  const response = await fetchDemoFixture("/demo-canned/default.json", options.signal);
  if (!response.ok) {
    throw new PredictionStreamError(`Demo fixture HTTP ${response.status}`);
  }
  const canned = (await response.json()) as CannedPrediction;
  const speed = Math.max(0.1, Math.min(5, options.demoSpeed || 4));
  let previousTime = 0;
  let payload: PredictionPayload | null = null;
  const gate = createSimulationStepGate(options.signal);

  for (const item of canned.events || []) {
    throwIfAborted(options.signal);
    await delay(Math.max(0, (item.t_ms - previousTime) / speed), options.signal);
    payload = await dispatchEventWithSimulationStepDelay(
      item.event,
      options.onEvent,
      gate,
    );
    if (payload) {
      break;
    }
    previousTime = item.t_ms;
  }

  if (!payload) {
    throw new PredictionStreamError("Demo stream ended without a final payload");
  }
  return payload;
}

export function consumeLine(
  line: string,
  onEvent: (event: PredictionStreamEvent) => void,
): PredictionPayload | null {
  const event = parseLine(line);
  return event ? dispatchEvent(event, onEvent) : null;
}

export async function streamPrediction(
  request: PredictionRequest,
  options: PredictionStreamOptions,
): Promise<PredictionPayload> {
  const session = captureSession();
  session.assertCurrent();
  const originalEvent = options.onEvent;
  options = { ...options, signal: sessionSignal(options.signal), onEvent: (event) => {
    session.assertCurrent();
    originalEvent(event);
  } };
  if (options.demo && isDemoMode()) return replayCannedPrediction(request, options);

  const response = await apiFetch(PREDICTION_STREAM_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(
      toV2PredictionRequest(
        request,
        options.historyReview || null,
        options.outputLanguage || "en",
      ),
    ),
    signal: options.signal,
  });
  session.assertCurrent();
  const historyId = response.headers.get("X-Oransim-History-Id");
  if (historyId && /^hist_[0-9a-f]{32}$/.test(historyId)) options.onHistoryId?.(historyId);
  if (!response.body) {
    throw new PredictionStreamError("Streaming response body is unavailable");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalPayload: PredictionPayload | null = null;
  const gate = createSimulationStepGate(options.signal);

  try {
    while (true) {
      throwIfAborted(options.signal);
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let lineBreak = buffer.indexOf("\n");
      while (lineBreak >= 0) {
        const line = buffer.slice(0, lineBreak);
        buffer = buffer.slice(lineBreak + 1);
        const event = parseLine(line);
        if (event) {
          finalPayload =
            (await dispatchEventWithSimulationStepDelay(
              event,
              options.onEvent,
              gate,
            )) ?? finalPayload;
        }
        if (finalPayload) {
          await reader.cancel();
          session.assertCurrent();
          return finalPayload;
        }
        lineBreak = buffer.indexOf("\n");
      }
    }
    buffer += decoder.decode();
    if (buffer.trim()) {
      const event = parseLine(buffer);
      if (event) {
        finalPayload =
          (await dispatchEventWithSimulationStepDelay(
            event,
            options.onEvent,
            gate,
          )) ?? finalPayload;
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (!finalPayload) {
    throw new PredictionStreamError(
      `Prediction stream at ${apiUrl(PREDICTION_STREAM_PATH)} ended without a final payload`,
    );
  }
  session.assertCurrent();
  return finalPayload;
}
